// Public job description service — no authentication.
//
// Path mapping (corrigé après E2E shard 3 toujours rouge sur pipelines
// 2544974327 → 2547037495) :
// - SPA route : `/jd/:token` (page React, QR-friendly, sert le HTML shell)
// - API JSON  : `/api/v1/public/job-descriptions/:token` (cf. routes.rs:176)
// Avant le fix, le service appelait `GET /jd/${token}` qui hit le shell SPA →
// `res.json()` plantait sur du HTML → page bloquée en error state générique.
//
// Le backend renvoie `PublicSignaturePayload` (cf. handlers/public_job_description.rs:40)
// avec un champ `snapshot` JSON qui contient les données figées au moment de
// la demande de signature (cf. build_snapshot in services/job_description_signature.rs).
// On flatten ici pour exposer le shape stable attendu par la page React.

import { PUBLIC_API_BASE, publicGet, publicPost } from "./public-fetch"

const API_PATH = "/api/v1/public/job-descriptions"

export type PublicJobDescriptionStatus =
  | "pending_staff"
  | "pending_manager"
  | "signed"
  | "revoked"
  | "expired"

export interface PublicJobDescriptionMission {
  id: string
  label: string
}

export interface PublicJobDescriptionSkill {
  id: string
  label: string
}

export interface PublicJobDescriptionQualiopiFunction {
  id: string
  code: string
  label: string
}

export interface PublicJobDescriptionResponse {
  status: PublicJobDescriptionStatus
  version_number: number
  signed_at: string | null
  staff_signed_at: string | null
  manager_signed_at: string | null
  revoked_at: string | null
  expires_at: string | null
  signer_role: "staff" | "manager"
  pdf_url: string | null
  tenant: {
    name: string
    logo_url: string | null
  }
  staff: {
    full_name: string
    job_title: string | null
    department: string | null
    employee_number: string | null
    email: string | null
    hire_date: string | null
  }
  missions: PublicJobDescriptionMission[]
  skills: PublicJobDescriptionSkill[]
  qualiopi_functions: PublicJobDescriptionQualiopiFunction[]
  manager: {
    full_name: string | null
  } | null
  /** Information RGPD du signataire, rédigée et servie par l'API. */
  rgpd_notice: string
}

export interface SignPublicJobDescriptionResponse {
  signature_id: string
  new_status: PublicJobDescriptionStatus
  is_final: boolean
  version_number: number
  signer_role: "staff" | "manager"
}

// Raw backend payload — mirroir de `PublicSignaturePayload`
// (src/api/handlers/public_job_description.rs:40-58).
export type RawPublicSignaturePayload = {
  signature_id: string
  tenant_id: string
  hr_staff_id: string
  mode: string
  mode_label: string
  version_number: number
  status: string
  snapshot: RawSnapshot
  snapshot_sha256: string
  snapshot_at: string | null
  signer_role: string
  token_expires_at: string | null
  already_signed_by_self: boolean
  staff_signed_at: string | null
  manager_signed_at: string | null
  rgpd_notice: string
}

type SnapshotLabels = string[] | null

interface RawSnapshot {
  staff?: {
    id?: string
    first_name?: string | null
    last_name?: string | null
    email?: string | null
    job_title?: string | null
    department?: string | null
    employee_number?: string | null
    hire_date?: string | null
  } | null
  employer?: {
    name?: string | null
    logo_url?: string | null
  } | null
  // build_snapshot fige missions et compétences en libellés bruts.
  missions?: SnapshotLabels
  skills?: SnapshotLabels
  qualiopi_functions?: Array<{ code?: string; label?: string }> | null
  manager?: { full_name?: string | null } | null
}

function toLabelled(prefix: string, labels: SnapshotLabels | undefined) {
  return (labels ?? []).map((label, idx) => ({ id: `${prefix}-${idx}`, label }))
}

function toStaff(staffSnap: NonNullable<RawSnapshot["staff"]>): PublicJobDescriptionResponse["staff"] {
  const fullName = [staffSnap.first_name, staffSnap.last_name]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(" ")
    .trim()
  return {
    full_name: fullName === "" ? "Collaborateur" : fullName,
    job_title: staffSnap.job_title ?? null,
    department: staffSnap.department ?? null,
    employee_number: staffSnap.employee_number ?? null,
    email: staffSnap.email ?? null,
    hire_date: staffSnap.hire_date ?? null,
  }
}

function toTenant(snap: RawSnapshot): PublicJobDescriptionResponse["tenant"] {
  const employerSnap = snap.employer ?? {}
  return {
    name: employerSnap.name ?? "Organisme de formation",
    logo_url: employerSnap.logo_url ?? null,
  }
}

function toQualiopiFunctions(snap: RawSnapshot): PublicJobDescriptionQualiopiFunction[] {
  return (snap.qualiopi_functions ?? []).map((fn, idx) => ({
    id: fn.code ?? `qf-${idx}`,
    code: fn.code ?? "",
    label: fn.label ?? "",
  }))
}

// Signed_at = max(staff_signed_at, manager_signed_at) — pour les états signed.
function signatureDates(raw: RawPublicSignaturePayload, status: PublicJobDescriptionStatus) {
  const staffSignedAt = raw.staff_signed_at ?? null
  const managerSignedAt = raw.manager_signed_at ?? null
  return {
    signed_at: status === "signed" ? managerSignedAt ?? staffSignedAt : null,
    staff_signed_at: staffSignedAt,
    manager_signed_at: managerSignedAt,
  }
}

export function toResponse(raw: RawPublicSignaturePayload): PublicJobDescriptionResponse {
  const snap = raw.snapshot ?? {}
  const status = (raw.status as PublicJobDescriptionStatus) ?? "pending_staff"
  return {
    status,
    version_number: raw.version_number,
    ...signatureDates(raw, status),
    revoked_at: null, // Backend ne renvoie pas explicitement revoked_at (status revoked = backend retourne 410).
    expires_at: raw.token_expires_at ?? null,
    signer_role: (raw.signer_role as "staff" | "manager") ?? "staff",
    pdf_url: null, // Le PDF n'est pas servi par l'endpoint public — la SPA ne propose pas le download dans cette version.
    tenant: toTenant(snap),
    staff: toStaff(snap.staff ?? {}),
    missions: toLabelled("mission", snap.missions),
    skills: toLabelled("skill", snap.skills),
    qualiopi_functions: toQualiopiFunctions(snap),
    manager: snap.manager ? { full_name: snap.manager.full_name ?? null } : null,
    rgpd_notice: raw.rgpd_notice,
  }
}

export function getPublicJobDescription(
  token: string,
): Promise<PublicJobDescriptionResponse> {
  return publicGet<RawPublicSignaturePayload>(`${API_PATH}/${token}`).then(
    toResponse,
  )
}

export function signPublicJobDescription(token: string) {
  // Backend exige `rgpd_acknowledged: true` (cf. handlers/public_job_description.rs:166-170).
  // La page React force déjà l'acceptation via la checkbox de consent
  // (`jd-consent-checkbox`) avant d'activer le bouton signer ; envoyer
  // `true` ici est cohérent avec l'UX et passe le guard server-side.
  return publicPost<SignPublicJobDescriptionResponse>(
    `${API_PATH}/${token}/sign`,
    { rgpd_acknowledged: true },
  )
}

export function publicJobDescriptionPdfUrl(token: string): string {
  // Endpoint PDF non encore exposé dans `/api/v1/public/job-descriptions/`.
  // On retourne l'URL "logique" — la page n'affiche le bouton que si
  // `jd.pdf_url` est non-null, ce qui n'est jamais le cas actuellement.
  return `${PUBLIC_API_BASE}${API_PATH}/${token}/pdf`
}
